<template>
    <section
        class="step-content-container step-content-container-trial__step-2 flex-level jc-center wp-10"
    >
        <div class="wp-5 p-8 acenter" style="max-width: 640px">
            <onboarding-trial-illustration :lang="currentLang"></onboarding-trial-illustration>
        </div>

        <div class="wp-5 p-8 bl-1 flex-column" style="max-width: 640px">
            <!-- title -->
            <div class="header">
                <h2 class="m-0">{{ $l10n.__('Créez votre compte') }}</h2>
                <p class="fs-3 gray-60 m-0 mb-3 semibold">
                    {{ $l10n.__('Afin de pouvoir démarrer votre essai') }}
                </p>
            </div>

            <!-- form -->
            <el-form ref="form" class="form" labelPosition="top" :model="form" :rules="rules">
                <el-form-item
                    :label="$t.__('common.labels.your_email_address')"
                    prop="login"
                    :error="errors.get('login')"
                >
                    <text-input
                        mode="edit"
                        :value="form.login"
                        :placeholder="
                            $t.__(
                                'pages.onboarding.trial.OnboardingTrialStep1.username_to_connect_to_sellsy',
                            )
                        "
                        @input="updateLogin"
                    />
                </el-form-item>
                <el-form-item
                    class="mb-4"
                    :label="$t.__('pages.onboarding.trial.OnboardingTrialStep1.define_password')"
                    prop="password"
                    :error="errors.get('password')"
                >
                    <password-input
                        mode="edit"
                        :value="form.password"
                        :placeholder="
                            $t.__(
                                'pages.onboarding.trial.OnboardingTrialStep1.to_secure_account_access',
                            )
                        "
                        @input="updatePassword"
                    />
                </el-form-item>
                <el-form-item class="mb-4" prop="terms" :error="errors.get('terms')">
                    <el-checkbox :value="form.terms" class="flex-row" @input="updateTerms">
                        <span
                            class="label label-nobackground p-0 fs-3"
                            v-html="termsLabelLegalInformation"
                        />
                        <p class="p-0 mt-2 mb-1 fs-3" v-html="termsLabelPrivatePolicy" />
                    </el-checkbox>
                </el-form-item>
                <el-form-item>
                    <el-checkbox class="flex-row" :value="form.mailing" @input="updateMailing">
                        <span class="label label-nobackground p-0 fs-3">
                            {{
                                $l10n.__(
                                    'Je souhaite être informé des actualités, publications et offres de Sellsy',
                                )
                            }}
                        </span>
                    </el-checkbox>
                </el-form-item>
            </el-form>

            <!-- call to action -->
            <div class="action self-center">
                <CompassButton
                    :label="
                        $t.__(
                            'pages.onboarding.trial.OnboardingTrialStep1.create_account_and_continue',
                        )
                    "
                    type="primary"
                    class="self-center"
                    :disabled="isDisabledNextStep"
                    :loading="loading"
                    @click="submitForm('form', 'goToNextStep')"
                />
            </div>

            <!-- separator -->
            <p class="bb-1 wp-10 my-4 acenter separator">
                <span class="grey white-bkg px-3">{{ $l10n.__('Ou') }}</span>
            </p>

            <!-- sign in google -->
            <div class="action self-center flex-column ai-center">
                <el-tooltip
                    :content="
                        $t.__(
                            'pages.onboarding.trial.OnboardingTrialStep1.please_accept_terms_before_registring_with_google',
                        )
                    "
                    placement="top"
                    :disabled="form.terms"
                >
                    <div>
                        <CompassButton
                            :label="$t.__('common.actions.subscribe_with_google')"
                            type="secondary"
                            :disabled="!form.terms"
                            @click="signUpGoogle()"
                        >
                            <template slot="prefix">
                                <SaasIcon icon="svgGoogle" :width="20" :height="20" class="mr-1" />
                            </template>
                        </CompassButton>
                    </div>
                </el-tooltip>
            </div>
        </div>
    </section>
</template>

<script>
import StepperMixin from 'mixins/StepperMixins';
import FormMixins from 'mixins/FormMixins';
import TextInput from 'vcomponents/inputs/TextInput.vue';
import PasswordInput from 'vcomponents/inputs/PasswordInput.vue';
import { createNamespacedHelpers } from 'vuex';
import OnboardingTrialApi from 'api/onboarding/trial/onboardingTrial.js';
import OnboardingTrialRules from 'vcomponents/forms/rules/OnboardingTrialRules';
import {
    generalConditionsPath,
    privacyPolicyPath,
    getGoSellsyUrl,
} from 'Config/authenticated/goSellsyPaths';
import OnboardingTrialIllustration from 'vcomponents/pages/onboarding/trial/OnboardingTrialIllustration.vue';
import SaasIcon from 'vcomponents/common/SaasIcon.vue';

const namespace = 'onboardingTrial';
const { mapGetters, mapActions } = createNamespacedHelpers(namespace);

export default {
    name: 'OnboardingTrialStep1',
    components: {
        SaasIcon,
        TextInput,
        PasswordInput,
        OnboardingTrialIllustration,
    },
    mixins: [StepperMixin, FormMixins],
    data() {
        return {
            loading: false,
            namespaceLocalStorage: namespace,
            pathImage: '/ressources/public/img/onboarding/Sellsy_produit_onboarding2.png',
            rules: OnboardingTrialRules,
        };
    },
    computed: {
        ...mapGetters({
            form: 'formStep1',
        }),
        legalInformationLang() {
            const acceptedLang = ['en', 'fr'];
            return acceptedLang.includes(this.currentLang) ? this.currentLang : 'fr';
        },
        termsLabelLegalInformation() {
            return this.$l10n.__(
                "En cochant cette case, vous déclarez avoir lu et accepté nos %(startLink)s Conditions Générales d'Utilisation %(endLink)s",
                {
                    startLink: `<a target='_blank' class='fs-3 semibold' href='${getGoSellsyUrl(
                        this.legalInformationLang,
                        generalConditionsPath,
                    )}'>`,
                    endLink: `</a>`,
                },
            );
        },
        termsLabelPrivatePolicy() {
            return this.$l10n.__(
                'Vous pouvez également consulter %(startLink)s notre politique de confidentialité %(endLink)s',
                {
                    startLink: `<a target='_blank' class='fs-3 semibold' href='${getGoSellsyUrl(
                        this.legalInformationLang,
                        privacyPolicyPath,
                    )}'>`,
                    endLink: `</a>`,
                },
            );
        },
    },
    mounted() {
        this.initStep();
        this.updateUtms(this.$route.query);
    },
    methods: {
        ...mapActions([
            'updateUtms',
            'initialiseStore',
            'updateLogin',
            'updatePassword',
            'updateTerms',
            'updateMailing',
        ]),
        /**
         * Go to the next step
         */
        /* eslint-disable-next-line vue/no-unused-properties */
        async goToNextStep() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            this.persistStateInLocalStorage(this.form);

            const token = await this.getTokenReCaptchaV3('onboardingTrialValidate');

            OnboardingTrialApi.validateStep(this.form, `credentials`, {
                'g-recaptcha-response': token,
            })
                .then(
                    () => {
                        this.$router.push({
                            name: this.$route.meta.next,
                            query: { lang: this.$route.query.lang },
                        });
                    },
                    (error) => {
                        Push.error(
                            this.$l10n.__("Une erreur est survenue lors de l'enregistrement"),
                            false,
                        );
                        this.errors.setErrors(error);
                    },
                )
                .finally(() => (this.loading = false));
        },
        /**
         * Sign up with google
         */
        signUpGoogle() {
            this.persistStateInLocalStorage(this.form);
            window.location.href = '/signup/google';
        },
    },
};
</script>

<style lang="scss" scoped>
.step-content-container {
    height: 528px;
    margin-top: auto;
    margin-bottom: auto;
}

.label {
    white-space: initial;
}
.separator {
    line-height: 0.1;
}
</style>

<style lang="scss">
.step-content-container-trial__step-2 {
    .el-form-item.is-error {
        margin-bottom: 0;
    }
    .el-form-item__error {
        position: relative;
    }
}
</style>
